import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSettingsStore } from '@/store/settingsStore';
import type { Organization } from '../api/organization.schemas';
import type { Project } from '@/features/projects/api/projects.schemas';
import type { DateRange } from '@/lib/schemas/dateRange.schemas';

/**
 * Hook to handle organization/project context switching
 */
export function useContextSwitcher() {
  const queryClient = useQueryClient();
  const {
    setSelectedOrganization,
    setSelectedProject,
    setSelectedDateRange,
    clearProjectContext,
  } = useSettingsStore();

  const switchOrganization = useCallback(async (organization: Organization) => {
    try {
      // Update local state (no backend call needed, similar to original Nuxt implementation)
      setSelectedOrganization(organization);
      
      // Clear project context since organization changed
      clearProjectContext();
      
      // Invalidate all dashboard and context-dependent queries
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['organization-permissions'] });
      await queryClient.invalidateQueries({ queryKey: ['project-permissions'] });
      
      console.log('Context switched to organization:', organization.name);
    } catch (error) {
      console.error('Failed to switch organization context:', error);
      throw error;
    }
  }, [setSelectedOrganization, clearProjectContext, queryClient]);

  const switchProject = useCallback(async (project: Project) => {
    try {
      // Update local state
      setSelectedProject(project);
      
      // Invalidate all dashboard queries to ensure fresh data for new project
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['project-permissions'] });
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      
      console.log('Context switched to project:', project.name);
    } catch (error) {
      console.error('Failed to switch project context:', error);
      throw error;
    }
  }, [setSelectedProject, queryClient]);

  const updateDateRange = useCallback(async (dateRange: DateRange) => {
    try {
      // Update local state
      setSelectedDateRange(dateRange);
      
      // Invalidate all dashboard queries to ensure fresh data for new date range
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      
      console.log('Date range updated:', { from: dateRange.from, to: dateRange.to });
    } catch (error) {
      console.error('Failed to update date range:', error);
      throw error;
    }
  }, [setSelectedDateRange, queryClient]);

  return {
    switchOrganization,
    switchProject,
    updateDateRange,
  };
}